/**
 * 1:1 port of `_parseResultStringToValue` / `_parseBetItemIdFromResult`
 * in packages/amgcarracing_v2/lib/presentation/screens/game_screen.dart
 * (lines 479-516, 779-797).
 *
 * Server returns the round result in one of four shapes:
 *   "Name,@ID"     e.g. "GLamborghini,@377" or "Ferrari,@265"
 *   "wID"          e.g. "w377"
 *   raw ID         e.g. "377"
 *   bare name      e.g. "GLamborghini"
 *
 * Multiplayer `StartPlay.Result` typically arrives as the bare name; the
 * Flutter bloc rebuilds the canonical `Name,@id` form before storing it.
 */
import type { BetType } from '../types/game';
import { BET_ITEM_IDS, BET_ITEM_ID, betTypeFromId } from '../types/game';

export interface ParsedResult {
  winningBetItemId: number;
  winner: BetType;
  /** Normalized canonical string `Name,@id` for logging / re-broadcast. */
  canonical: string;
}

export function parseResultString(raw: string): ParsedResult {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) throw new Error('Empty result string');

  const id = parseBetItemId(trimmed);
  if (id == null) {
    throw new Error(`Unrecognised result format: ${raw}`);
  }
  const winner = betTypeFromId(id);
  if (!winner) {
    throw new Error(`Unknown winning betItemId: ${id}`);
  }
  return {
    winningBetItemId: id,
    winner,
    canonical: `${winner},@${id}`,
  };
}

/**
 * Try each format until one extracts a known bet-item id (370..377).
 * Returns null if nothing matches.
 */
function parseBetItemId(raw: string): number | null {
  // "Name,@ID" — split, prefer the ID after '@', fall back to the name.
  if (raw.includes(',')) {
    const parts = raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
    for (const part of parts) {
      const id = parseBetItemId(part);
      if (id != null) return id;
    }
    return null;
  }

  // "@ID" or "wID" — extract a number, validate it's in the known set.
  const m = /-?\d+/.exec(raw);
  if (m) {
    const n = parseInt(m[0], 10);
    if (Number.isFinite(n) && BET_ITEM_IDS.includes(n)) return n;
  }

  // Bare name — try BET_ITEM_ID lookup directly.
  const direct = (BET_ITEM_ID as Record<string, number>)[raw];
  if (typeof direct === 'number') return direct;

  return null;
}
